import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ShareModal from '../components/ShareModal';
import EditProjectModal from '../components/EditProjectModal';
import { getCertificationAcronym } from '../constants/certifications';
import { SERVICENOW_PRODUCTS, SECURITY_CLEARANCE_LEVELS } from '../utils/servicenowData';
import BackToResultsButton from '../components/BackToResultsButton';
import { hasNavigationState } from '../utils/navigationState';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Users, 
  Briefcase, 
  Globe, 
  Clock,
  Award,
  Languages,
  Laptop,
  Edit,
  Archive,
  Trash2,
  Search,
  Share2,
  FileText,
  Download,
  Eye,
  Star,
  MessageSquare,
  Send,
  Brain,
  CheckCircle,
  RefreshCw,
  Zap,
  Shield,
  Sparkles,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language to flag emoji mapping
const LANGUAGE_FLAGS = {
  'English': '🇬🇧',
  'French': '🇫🇷',
  'German': '🇩🇪',
  'Spanish': '🇪🇸',
  'Italian': '🇮🇹',
  'Portuguese': '🇵🇹',
  'Dutch': '🇳🇱',
  'Russian': '🇷🇺',
  'Chinese (Mandarin)': '🇨🇳',
  'Japanese': '🇯🇵',
  'Korean': '🇰🇷',
  'Arabic': '🇸🇦',
  'Hindi': '🇮🇳',
  'Polish': '🇵🇱',
  'Swedish': '🇸🇪',
  'Danish': '🇩🇰',
  'Norwegian': '🇳🇴',
  'Finnish': '🇫🇮',
  'Turkish': '🇹🇷',
  'Greek': '🇬🇷',
  'Czech': '🇨🇿',
  'Romanian': '🇷🇴',
  'Hungarian': '🇭🇺',
  'Hebrew': '🇮🇱',
  'Thai': '🇹🇭',
  'Vietnamese': '🇻🇳',
  'Indonesian': '🇮🇩',
  'Malay': '🇲🇾'
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(true);

  useEffect(() => {
    fetchProjectDetails();
    if (user?.role === 'resource_manager') {
      checkIfFavorited();
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project details');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleFindMatches = () => {
    navigate(`/matching?project=${projectId}`);
  };

  const handleEdit = () => {
    // Navigate to ProjectsPageNew with state to trigger the modern edit modal
    navigate('/projects', { state: { editProjectId: projectId } });
  };

  const handleSaveProject = async (updatedData) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
      toast.success('Project updated successfully');
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
      throw error;
    }
  };

  const handleToggleActivation = async () => {
    const token = localStorage.getItem('token');
    const endpoint = project.is_activated ? 'deactivate' : 'activate';
    
    try {
      await axios.post(`${API}/projects/${projectId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Project ${project.is_activated ? 'archived' : 'activated'} successfully`);
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const checkIfFavorited = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/favorites/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const favorited = response.data.some(fav => 
        (fav.favorite?.project_id || fav.project?.id) === projectId
      );
      setIsFavorited(favorited);
    } catch (error) {
      console.error('Failed to check favorite status');
    } finally {
      setCheckingFavorite(false);
    }
  };

  const handleToggleFavorite = async () => {
    const token = localStorage.getItem('token');
    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/project/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Removed from favorites');
        setIsFavorited(false);
      } else {
        await axios.post(`${API}/favorites/project/${projectId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Added to favorites');
        setIsFavorited(true);
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleContactPM = async () => {
    if (project?.company_id) {
      // For RMs, get the selected resource info from navigation state or first resource
      let resourceInfo = null;
      
      if (user?.role === 'resource_manager') {
        const token = localStorage.getItem('token');
        try {
          // Try to get resource from navigation state first
          const navState = sessionStorage.getItem('navigationState');
          let resourceId = navState ? JSON.parse(navState).selectedResource : null;
          
          // If no resource in state, get user's resources
          const response = await axios.get(`${API}/resources/my`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const resources = response.data;
          
          if (resources.length > 0) {
            // Use the resource from state, or first resource if not found
            const resource = resourceId 
              ? resources.find(r => r.id === resourceId) || resources[0]
              : resources[0];
            
            resourceInfo = {
              id: resource.id,
              name: `${resource.first_name} ${resource.last_name}`,
              role: resource.role || resource.roles?.[0]
            };
          }
        } catch (error) {
          console.error('Failed to get resource info:', error);
        }
      }
      
      // Build prefilled message
      let prefilledMessage = `Hello,\n\nI am interested in the project "${project.title}"`;
      if (resourceInfo) {
        prefilledMessage += ` for my resource ${resourceInfo.name}`;
      }
      prefilledMessage += `.\n\nBest regards`;
      
      navigate('/messages', {
        state: {
          targetCompanyId: project.company_id,
          projectInfo: {
            id: project.id,
            title: project.title
          },
          resourceInfo: resourceInfo,
          prefilledMessage: prefilledMessage
        }
      });
    }
  };

  const handleApplyForProject = async () => {
    // Get user's resources to allow selection
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const resources = response.data;
      
      if (resources.length === 0) {
        toast.error('You need to create a resource first before applying');
        navigate('/resources');
        return;
      }
      
      // If only one resource, apply directly
      if (resources.length === 1) {
        await applyWithResource(resources[0].id);
      } else {
        // Show resource selection (simplified version - just use first resource for now)
        await applyWithResource(resources[0].id);
      }
    } catch (error) {
      toast.error('Failed to load resources');
    }
  };

  const applyWithResource = async (resourceId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/pre-reservations/apply`, null, {
        params: {
          project_id: projectId,
          resource_id: resourceId
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply for project');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const isOwner = user?.company_id === project.company_id;
  const isPM = user?.role === 'project_manager';

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-6 py-8 ">
        {/* Header */}
        <div className="mb-6">
          <BackToResultsButton />
          {!hasNavigationState() && (
            <Button
              variant="ghost"
              onClick={() => navigate('/projects')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Button>
          )}

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-mono rounded">
                  {project.project_id}
                </span>
                <Badge style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
                  {project.project_type}
                </Badge>
                {project.is_activated ? (
                  <Badge className="bg-green-100 text-green-700">
                    🟢 Active
                  </Badge>
                ) : (
                  <Badge className="bg-gray-100 text-gray-600">
                    ⚫ Archived
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
                {project.title}
              </h1>
            </div>

            {/* Actions */}
            {isOwner && isPM && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowShareModal(true)}
                  className="btn-snow"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button variant="outline" onClick={handleEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleToggleActivation}
                  className={project.is_activated ? 'text-orange-600' : 'text-green-600'}
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {project.is_activated ? 'Archive' : 'Activate'}
                </Button>
                <Button variant="outline" onClick={handleDelete} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="md:col-span-2 space-y-6">
            
            {/* Project Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Project Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words overflow-wrap-anywhere">
                  {project.description}
                </p>
              </CardContent>
            </Card>

            {/* Skills & Certifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Skills & Certifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Required Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {project.required_skills?.map((skill, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                {project.required_certifications && project.required_certifications.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">ServiceNow Certifications</p>
                    <div className="flex flex-wrap gap-2">
                      {project.required_certifications.map((cert, idx) => (
                        <Badge 
                          key={idx} 
                          style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}
                          title={cert}
                        >
                          {getCertificationAcronym(cert)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {project.other_certifications && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Other Certifications</p>
                    <p className="text-sm text-gray-600">{project.other_certifications}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ServiceNow Products & Preferences */}
            {((project.servicenow_products && project.servicenow_products.length > 0) || 
              project.prefer_ai_augmented || 
              project.has_security_requirement) && (
              <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-purple-800">
                    <Sparkles className="w-5 h-5 mr-2" />
                    ServiceNow Products & Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* ServiceNow Products */}
                  {project.servicenow_products && project.servicenow_products.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-2">
                        ServiceNow Products Required
                        <span className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs rounded-full">Primary Matching</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {project.servicenow_products.map(productId => {
                          const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
                          return product && (
                            <Badge key={productId} className="bg-white text-purple-700 border border-purple-200" title={product.fullName}>
                              {product.name} <span className="text-purple-400 text-xs ml-1">({product.category})</span>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* AI-Augmented Preference */}
                  {project.prefer_ai_augmented && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center gap-2 text-purple-700">
                        <Zap className="w-5 h-5" />
                        <span className="font-medium">Prefers AI-Augmented Consultants</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        This project prefers consultants who actively use AI tools to enhance their productivity and deliverables.
                      </p>
                    </div>
                  )}
                  
                  {/* Security Requirements */}
                  {project.has_security_requirement && (
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700 mb-3">
                        <Shield className="w-5 h-5" />
                        <span className="font-medium">Security Requirements</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {project.required_clearance_level && (
                          <div>
                            <p className="text-gray-500 text-xs">Clearance Level</p>
                            <p className="font-medium text-gray-800">
                              {SECURITY_CLEARANCE_LEVELS.find(l => l.id === project.required_clearance_level)?.name || project.required_clearance_level}
                            </p>
                          </div>
                        )}
                        {project.clearance_valid_country && (
                          <div>
                            <p className="text-gray-500 text-xs">Clearance Valid In</p>
                            <p className="font-medium text-gray-800">{project.clearance_valid_country}</p>
                          </div>
                        )}
                        {project.requires_local_presence && (
                          <div>
                            <p className="text-gray-500 text-xs">Local Presence</p>
                            <p className="font-medium text-amber-700">Required</p>
                          </div>
                        )}
                        {project.nationality_restriction && (
                          <div>
                            <p className="text-gray-500 text-xs">Nationality Restriction</p>
                            <p className="font-medium text-gray-800">{project.nationality_restriction}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {project.languages_required && project.languages_required.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Languages className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    Language Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {project.languages_required.map((lang, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg"
                      >
                        <span className="text-2xl">
                          {LANGUAGE_FLAGS[lang.language] || '🌐'}
                        </span>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#1B3240' }}>
                            {lang.language}
                          </p>
                          <Badge 
                            className="text-xs mt-1" 
                            style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}
                          >
                            {lang.level}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Attachments */}
            {project.attachments && project.attachments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                      Project Documents ({project.attachments.length})
                    </div>
                    {/* AI Analysis Status */}
                    {project.attachments.some(a => a.text_content) ? (
                      <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        <Brain className="w-3 h-3" />
                        <span>AI Ready</span>
                        <CheckCircle className="w-3 h-3" />
                      </div>
                    ) : isOwner && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                        onClick={async () => {
                          const token = localStorage.getItem('token');
                          try {
                            const response = await axios.post(
                              `${API}/projects/${project.id}/analyze-attachments`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (response.data.successful_count > 0) {
                              toast.success(`${response.data.successful_count} document(s) analyzed for AI matching`);
                              fetchProjectDetails();
                            } else {
                              toast.info('No documents could be analyzed');
                            }
                          } catch (error) {
                            toast.error('Failed to analyze documents');
                          }
                        }}
                      >
                        <Brain className="w-3 h-3 mr-1" />
                        Analyze for AI
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.attachments.map((attachment, idx) => {
                      const filename = attachment.filename || attachment.stored_filename || 'document';
                      const storedName = attachment.stored_filename || attachment;
                      const fileExt = filename.split('.').pop().toLowerCase();
                      const hasTextContent = !!attachment.text_content;
                      
                      return (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="p-2 bg-white rounded-lg border border-gray-200">
                              <FileText className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">
                                {filename}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-500">
                                  {fileExt === 'pdf' && 'PDF Document'}
                                  {fileExt === 'docx' && 'Word Document'}
                                  {fileExt === 'xlsx' && 'Excel Spreadsheet'}
                                  {fileExt === 'doc' && 'Word Document'}
                                  {fileExt === 'xls' && 'Excel Spreadsheet'}
                                  {!['pdf', 'docx', 'xlsx', 'doc', 'xls'].includes(fileExt) && 'Document'}
                                </p>
                                {hasTextContent && (
                                  <span className="text-xs text-green-600 flex items-center gap-0.5">
                                    <Brain className="w-3 h-3" /> AI
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`${BACKEND_URL}/api/projects/${project.id}/attachments/${storedName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-white rounded-lg transition-colors"
                              title="View document"
                            >
                              <Eye className="w-4 h-4 text-gray-600" />
                            </a>
                            <a
                              href={`${BACKEND_URL}/api/projects/${project.id}/attachments/${storedName}?download=true`}
                              className="p-2 hover:bg-white rounded-lg transition-colors"
                              title="Download document"
                            >
                              <Download className="w-4 h-4 text-gray-600" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isOwner && isPM ? (
                  <Button 
                    className="w-full btn-snow px-2 sm:px-4 py-2"
                    onClick={handleFindMatches}
                  >
                    <Search className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="hidden xs:inline sm:inline">Find Matching Resources</span>
                    <span className="inline xs:hidden sm:hidden">Find Matches</span>
                  </Button>
                ) : user?.role === 'resource_manager' ? (
                  <>
                    <Button 
                      className="w-full px-2 sm:px-4 py-2"
                      variant={isFavorited ? "default" : "outline"}
                      onClick={handleToggleFavorite}
                      disabled={checkingFavorite}
                    >
                      <Star className={`w-4 h-4 mr-1 sm:mr-2 flex-shrink-0 ${isFavorited ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                      <span>{isFavorited ? 'Favorited' : 'Add to Favorites'}</span>
                    </Button>
                    <Button 
                      className="w-full px-2 sm:px-4 py-2"
                      variant="outline"
                      onClick={handleContactPM}
                    >
                      <MessageSquare className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span>Contact PM</span>
                    </Button>
                    <Button 
                      className="w-full btn-snow px-2 sm:px-4 py-2"
                      onClick={handleApplyForProject}
                    >
                      <Send className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span>Apply for Project</span>
                    </Button>
                  </>
                ) : null}
              </CardContent>
            </Card>

            {/* Budget & Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Budget
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Hide budget from RMs if PM has enabled hide_budget option */}
                {project.hide_budget && user?.role === 'resource_manager' ? (
                  <div className="text-center py-4">
                    <Lock className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500 italic">Budget hidden by Project Manager</p>
                    <p className="text-xs text-gray-400 mt-1">Contact the PM for budget details</p>
                  </div>
                ) : project.price_amount && project.currency && project.price_type ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Rate</p>
                      <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                        {project.currency === 'EUR' ? '€' : project.currency === 'USD' ? '$' : project.currency === 'GBP' ? '£' : project.currency}
                        {project.price_amount.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">per {project.price_type}</p>
                    </div>
                    {project.price_equivalent_hourly && (
                      <div className="pt-3 border-t">
                        <p className="text-xs text-gray-500">Hourly equivalent</p>
                        <p className="text-sm font-medium text-gray-700">
                          {project.currency === 'EUR' ? '€' : project.currency === 'USD' ? '$' : project.currency}
                          {project.price_equivalent_hourly.toFixed(2)}/h
                        </p>
                      </div>
                    )}
                  </>
                ) : project.budget && project.budget_type ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Budget</p>
                    <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                      €{project.budget.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">{project.budget_type}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">Budget TBD</p>
                )}

                <div className="pt-3 border-t">
                  <p className="text-sm text-gray-600 mb-1">Commercial Model</p>
                  <Badge className="bg-purple-100 text-purple-700">
                    {project.commercial_model}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {project.duration_value && project.duration_unit && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Duration</p>
                    <p className="text-lg font-semibold" style={{ color: '#1B3240' }}>
                      {project.duration_value} {project.duration_unit}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-600 mb-1">Start Date</p>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(project.start_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Team Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Consultants Needed</p>
                  <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                    {project.number_of_consultants}
                  </p>
                </div>

                {project.occupation_rate && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-600 mb-1">Occupation Rate</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {project.occupation_rate}%
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location & Work Arrangement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Location & Work
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Location</p>
                  <p className="text-gray-900">{project.city}, {project.country}</p>
                </div>

                {project.remote_possible && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Remote Work</p>
                    <div className="space-y-2">
                      <Badge className="bg-blue-100 text-blue-700">
                        {project.remote_percentage}% Remote
                      </Badge>
                      {project.remote_zone && project.remote_zone !== 'worldwide' && (
                        <p className="text-sm text-gray-600">
                          Zone: {project.remote_zone_detail || project.remote_zone}
                        </p>
                      )}
                      {project.remote_zone === 'worldwide' && (
                        <p className="text-sm text-gray-600">🌍 Worldwide remote</p>
                      )}
                      {project.timezone_restrictions && (
                        <p className="text-sm text-gray-600">
                          Timezone: {project.timezone_restrictions}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Equipment</p>
                  <Badge className={project.equipment_provided ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {project.equipment_provided ? '✓ Provided' : '✗ Own'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Share Modal */}
      {project && (
        <ShareModal 
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          project={project}
        />
      )}

      {/* Edit Project Modal */}
      {project && (
        <EditProjectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          project={project}
          onSave={handleSaveProject}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
